import React, { Suspense, useState, useCallback } from "react";
import { Link, Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Home, ShieldOff } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { useAppStore } from "../../store";
import { ProjectProvider } from "../../contexts/ProjectContext";
import { TenantProvider, useTenantContext } from "../../contexts/TenantContext";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { PageLayout } from "./PageLayout";
import { PageProvider } from "./PageContext";
import { useSecurityContext } from "../../contexts/SecurityContext";
import { ContentLoadingFallback, PageLoadingFallback } from "../ui/LoadingSpinner";
import { Button } from "../ui/button";
import { useTranslation } from "../../lib/hooks/use-translation";
import { evaluatePermissionAccessState, getSidebarRoutePolicy, type PermissionAccessState, type RouteAccessEvaluator } from "../../lib/constants/routes/access-policy";
import { normalizeLegacyRoutePath, ROUTES } from "../../lib/constants/routes/route-paths";

interface AppLayoutProps {}

interface LayoutAccessEvaluator extends RouteAccessEvaluator {
  pathname: string;
}

export function resolveLayoutAccessState(evaluator: LayoutAccessEvaluator): PermissionAccessState {
  const policy = getSidebarRoutePolicy(evaluator.pathname);
  return evaluatePermissionAccessState(policy?.requiredPermissions, evaluator, policy?.permissionMatch);
}

const TenantScopedOutlet: React.FC = () => {
  const { activeTenantId, isLoading } = useTenantContext();
  const scopeKey = activeTenantId.length > 0 ? activeTenantId : isLoading ? "loading" : "none";
  return (
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet key={scopeKey} />
    </Suspense>
  );
};

const AuthorizationPending: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="mb-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="authorization-pending-banner">
      {t("common.loadingPlatformPermissions")}
    </div>
  );
};

const AccessDenied: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section
      className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-6 py-12"
      data-testid="access-denied"
      role="status"
      aria-live="polite"
      aria-labelledby="access-denied-title"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgb(var(--muted-rgb)/0.55)_0%,transparent_62%)]"
      />
      <div className="relative flex w-full max-w-[22rem] flex-col items-center text-center">
        <div className="surface-control mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-border shadow-elevation1">
          <ShieldOff className="h-6 w-6 text-muted-foreground" strokeWidth={1.75} aria-hidden />
        </div>
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground/75">{t("common.accessDeniedCode")}</p>
        <p id="access-denied-title" className="mt-2 text-xl font-semibold tracking-tight text-foreground">
          {t("common.accessDeniedTitle")}
        </p>
        <p className="mt-1.5 max-w-[18rem] text-xs text-muted-foreground">{t("common.accessDeniedMessage")}</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-9 gap-1.5 px-3.5" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            {t("notFound.goBack")}
          </Button>
          <Button type="button" size="sm" className="h-9 gap-1.5 px-3.5" asChild>
            <Link to={ROUTES.HOME}>
              <Home className="h-3.5 w-3.5" aria-hidden />
              {t("notFound.goHome")}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export const AppLayout: React.FC<AppLayoutProps> = () => {
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleMobileMenu = useCallback(() => setMobileMenuOpen((prev) => !prev), []);
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  React.useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.style.removeProperty("overflow");
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.removeProperty("overflow");
    };
  }, [mobileMenuOpen]);

  const { state: securityState, checkPermission } = useSecurityContext();
  const location = useLocation();
  const normalizedPath = normalizeLegacyRoutePath(location.pathname);
  if (normalizedPath !== location.pathname) {
    return <Navigate to={`${normalizedPath}${location.search}${location.hash}`} replace />;
  }
  if (!securityState.sessionHydrated) {
    return <PageLoadingFallback testId="session-loading-spinner" />;
  }
  if (!securityState.isAuthenticated) {
    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }
  const accessState = resolveLayoutAccessState({
    pathname: normalizedPath,
    authenticated: securityState.isAuthenticated,
    policySnapshotReady: securityState.policySnapshotReady,
    policySnapshotError: securityState.policySnapshotError,
    hasAnyPermission: (permissions) => permissions.some((permission) => checkPermission(permission)),
  });
  return (
    <div className="surface-canvas flex h-screen min-h-0 text-foreground">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={closeMobileMenu} aria-hidden>
          <div className="fixed inset-0 bg-black/50" />
        </div>
      )}

      <div className={cn("hidden lg:flex lg:flex-shrink-0", sidebarCollapsed ? "lg:w-sidebar-collapsed" : "lg:w-sidebar")}>
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-y-0 left-0 z-50 w-64 lg:hidden">
          <Sidebar collapsed={false} onToggle={closeMobileMenu} />
        </div>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <ProjectProvider>
          <TenantProvider>
            <PageProvider>
              <Header onToggleSidebar={toggleSidebar} sidebarCollapsed={sidebarCollapsed} onToggleMobileMenu={toggleMobileMenu} mobileMenuOpen={mobileMenuOpen} />
              <main className="surface-canvas flex min-h-0 flex-1 flex-col overflow-auto">
                {accessState === "denied" ? (
                  <AccessDenied />
                ) : (
                  <PageLayout>
                    {accessState === "pending" ? <AuthorizationPending /> : null}
                    <TenantScopedOutlet />
                  </PageLayout>
                )}
              </main>
            </PageProvider>
          </TenantProvider>
        </ProjectProvider>
      </div>
    </div>
  );
};

export default AppLayout;
